import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { isArray, isNil } from 'lodash-es'
import {
  AmountFormat,
  AuditDetail,
  AxeTradeCondition,
  CollateralBasketType,
  Collection,
  CollectionType,
  CounterpartyInstruction,
  CouponFrequency,
  DealDetail,
  Direction,
  DirectionFormat,
  EvergreenNoticePeriod,
  HaircutSign,
  Market,
  NegotiableFieldRequired,
  NegotiationDetail,
  NegotiationType,
  Order,
  OrderDetail,
  OrderDetailSummary,
  OrderSummary,
  OrderTerm,
  Organisation,
  OrganisationType,
  ReferenceRate,
  ServicingType,
  SettlementVenue,
  TradeConditionFlag,
  TradingEntity,
  TradingEntityType,
  TradingStatus,
  User
} from 'api'
import {
  AssetTypeHelper,
  AxeHelper,
  BASIS_POINTS_DECIMAL_SHIFT,
  CallableTypeHelper,
  CollateralIdentifierTypeHelper,
  CollateralTypeHelper,
  CollectionTypeHelper,
  CouponFrequencyHelper,
  DataHelper,
  DecisionMakerHelper,
  EvergreenNoticeHelper,
  HaircutTypeHelper,
  IndexLagHelper,
  IssueHelper,
  makeDirectionHelper,
  MarketTypeHelper,
  OrderTermHelper,
  OrganisationTypeHelper,
  PERCENTAGE_DECIMAL_SHIFT,
  RateTypeHelper,
  RepoClassificationHelper,
  RepoTypeHelper,
  RFQTypeHelper,
  ServicingTypeHelper,
  SettlementVenueHelper,
  StartTimeZoneHelper,
  TradeConditionFlagHelper,
  TradingEntityProductPermissionHelper,
  TradingEntityWithAliasHelper,
  TradingTypeHelper,
  UnassignedCollectionName
} from 'model'
import { makeNumberFilterGetter } from './baseFormatters'
import { Locale, NumberFormatOptions, Translation } from 'locale'
import {
  getCollectionNameText,
  KeyCreatorParams,
  TypedGetQuickFilterTextParams,
  TypedHeaderValueGetterParams,
  TypedValueGetterParams
} from 'view/components/datagrid'
import { compareInsensitive, Decimal, getFormattedTime } from 'utils'
import { RowDataGetter } from './groupRowFormatters'
import { isNotNil } from 'utils/predicate'
import { tradingStatusTranslations } from 'view/routes/admin/product/utils'

export function collectionKeyCreator(params: KeyCreatorParams<Collection>) {
  return params.value ? `${params.value.name}-${params.value.id}` : UnassignedCollectionName
}

export function containerKeyCreator(params: KeyCreatorParams<Collection>) {
  // by returning null we avoid grouping by container when there's no value
  return params.value ? `${params.value.name}-${params.value.id}` : ((null as unknown) as string)
}

export function groupedStandardCollectionKeyCreator(params: KeyCreatorParams<Collection>) {
  return params.value
    ? params.value.type === CollectionType.SEND_INDIVIDUALLY
      ? `${params.value.name}`
      : `${params.value.name}-${params.value.id}`
    : UnassignedCollectionName
}

export function parseCollectionKey(key: string) {
  return key === UnassignedCollectionName
    ? UnassignedCollectionName
    : key
        .split('-')
        .slice(0, -1)
        .join('-')
}

export function collectionFormatter(params: ValueFormatterParams) {
  // If node exists, assume grid value, otherwise assume filter value
  if (params.node) {
    return isNil(params.value) && params.node.group ? '' : getCollectionNameText(params.value)
  } else {
    return parseCollectionKey(params.value)
  }
}

export function safeValueGetter<TData, TResponse>(callback: (data: TData) => TResponse) {
  return function sanitizedValueGetter(params: ValueGetterParams<TData>) {
    if (!params?.data) {
      return null
    }

    return callback(params.data)
  }
}

export function collectionFilterFormatter(params: ValueFormatterParams) {
  return getCollectionNameText(params.value)
}

const isOrderDetail = (order: Order | OrderSummary | null): order is Order => {
  return !!order?.hasOwnProperty('orderId')
}

export const makeCounterpartyInstructionValueGetter = <K extends keyof CounterpartyInstruction, J extends keyof Order>(
  counterpartyKey: K,
  orderKey: J
) => {
  return (order: Order | OrderSummary | null) => {
    if (!isOrderDetail(order)) {
      return null
    }

    const counterpartyInstructions = order?.negotiationInstruction.counterpartyInstructions

    if (
      !counterpartyInstructions ||
      counterpartyInstructions.length === 0 ||
      counterpartyInstructions.every(counterpartyInstruction => counterpartyInstruction[counterpartyKey] === null)
    ) {
      return order[orderKey]
    }

    if (
      !counterpartyInstructions.every(
        counterpartyInstruction => counterpartyInstruction[counterpartyKey] === counterpartyInstructions[0][counterpartyKey]
      )
    ) {
      return Locale.translate(Translation.SC_MULTIPLE)
    }

    return counterpartyInstructions[0][counterpartyKey]
  }
}

type TradeDetail = OrderDetail | OrderDetailSummary | NegotiationDetail | AuditDetail | DealDetail

export function getIsin(tradeDetail: TradeDetail) {
  return (
    tradeDetail.collateral?.isin ??
    (tradeDetail.productSector?.basketType === CollateralBasketType.DBV
      ? tradeDetail.collateralBasket?.collateralBasketIsin
      : tradeDetail.collateralBasket?.id)
  )
}

export function makeIsinGetter(rowDataGetter?: RowDataGetter<TradeDetail>) {
  return function isinGetter(params: TypedValueGetterParams<TradeDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? getIsin(rowData) : ''
  }
}

export function getCusip(tradeDetail: TradeDetail) {
  return (
    tradeDetail.collateral?.cusip ??
    (tradeDetail.productSector?.basketType === CollateralBasketType.BONY ||
    (tradeDetail.productSector?.basketType === CollateralBasketType.GC_BESPOKE && tradeDetail.product?.market === Market.US)
      ? tradeDetail.collateralBasket?.collateralBasketCusip
      : null)
  )
}

export function makeCusipGetter(rowDataGetter?: RowDataGetter<TradeDetail>) {
  return (params: TypedValueGetterParams<TradeDetail>) => {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? getCusip(rowData) : ''
  }
}

type NegotiationOrDealDetail = NegotiationDetail | DealDetail | AuditDetail
type SupportedTradingEntityFieldNames = keyof Pick<TradingEntity, 'bic'>
type SupportedServicingDealTradingEntityFieldNames = keyof Pick<TradingEntity, 'lei' | 'bic'>
type SupportedOrganisationFieldNames = keyof Pick<Organisation, 'legalName' | 'lei'>

function isDeal(negotiationOrDealDetail: NegotiationOrDealDetail): negotiationOrDealDetail is DealDetail {
  return (negotiationOrDealDetail as DealDetail).deal !== undefined
}

function isUsMarket(negotiationOrDealDetail: NegotiationOrDealDetail) {
  const market = isDeal(negotiationOrDealDetail) ? negotiationOrDealDetail.deal.market : negotiationOrDealDetail.negotiation.market
  return market === Market.US
}

export function getOrganisationField<F extends SupportedOrganisationFieldNames>(
  negotiationOrDeal: NegotiationOrDealDetail,
  field: F,
  self: boolean
): Organisation[F] | '' {
  const { orderFundFirm, counterpartyFirm, clearingHouse, ownOrder } = negotiationOrDeal
  if (ownOrder) {
    if (self) {
      return isNil(orderFundFirm) ? '' : orderFundFirm[field]
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(counterpartyFirm) ? '' : counterpartyFirm[field]) : ''
    }
  } else {
    if (self) {
      return isNil(counterpartyFirm) ? '' : counterpartyFirm[field]
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(orderFundFirm) ? '' : orderFundFirm[field]) : ''
    }
  }
}

export function makeOrganisationFieldGetter(
  field: SupportedOrganisationFieldNames,
  self: boolean,
  rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>
) {
  return function organisationGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getOrganisationField(rowData, field, self)
    } else {
      return ''
    }
  }
}

export function getTradingEntityField<F extends SupportedTradingEntityFieldNames>(
  negotiationOrDeal: NegotiationOrDealDetail,
  field: F,
  self: boolean
): TradingEntity[F] | '' {
  const { ownOrder, orderFund, clearingHouse, counterparty } = negotiationOrDeal

  if (ownOrder) {
    if (self) {
      return isNil(orderFund) ? '' : orderFund[field]
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(counterparty) ? '' : counterparty[field]) : clearingHouse[field]
    }
  } else {
    if (self) {
      return isNil(counterparty) ? '' : counterparty[field]
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(orderFund) ? '' : orderFund[field]) : clearingHouse[field]
    }
  }
}

export function getServicingDealTradingEntityField<F extends SupportedServicingDealTradingEntityFieldNames>(
  dealDetail: DealDetail,
  field: F,
  self: boolean
): TradingEntity[F] | '' {
  const { ownOrder, servicingSideDetailsMeta } = dealDetail
  const fund = servicingSideDetailsMeta?.FUND_ID
  const counterparty = servicingSideDetailsMeta?.COUNTERPARTY_ID

  if (ownOrder) {
    if (self) {
      return isNil(fund) ? '' : fund[field]
    } else {
      return isNil(counterparty) ? '' : counterparty[field]
    }
  } else {
    if (self) {
      return isNil(counterparty) ? '' : counterparty[field]
    } else {
      return isNil(fund) ? '' : fund[field]
    }
  }
}

export function getTradingEntityLei(negotiationOrDeal: NegotiationOrDealDetail, self: boolean): string {
  const { ownOrder, orderFund, clearingHouse, counterparty } = negotiationOrDeal

  if (ownOrder) {
    if (self) {
      return isNil(orderFund) ? '' : orderFund.lei
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(counterparty) ? '' : counterparty.lei) : clearingHouse.lei
    }
  } else {
    if (self) {
      return isNil(counterparty) ? '' : counterparty.lei
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal) ? (isNil(orderFund) ? '' : orderFund.lei) : clearingHouse.lei
    }
  }
}

const getNegotiatedValue = <T>(field: NegotiableFieldRequired<T> | null | undefined) => {
  return field ? (field.modified ? field.modifiedValue : field.negotiated) : null
}

export function getServicingDealInvestmentDecisionMakerId(dealDetail: DealDetail, self: boolean): string | null {
  const { ownOrder, servicingSideDetails } = dealDetail
  const initiatorInvestmentDecisionMakerId = getNegotiatedValue(
    servicingSideDetails?.negotiationFields.INITIATOR_INVESTMENT_DECISION_MAKER?.negotiableFieldRequired
  )
  const responderInvestmentDecisionMakerId = getNegotiatedValue(
    servicingSideDetails?.negotiationFields.RESPONDER_INVESTMENT_DECISION_MAKER?.negotiableFieldRequired
  )

  if ((ownOrder && self) || (!ownOrder && !self)) {
    return initiatorInvestmentDecisionMakerId
  } else {
    return responderInvestmentDecisionMakerId
  }
}

export function getInvestmentDecisionMakerName(negotiationOrDeal: NegotiationOrDealDetail, self: boolean): string {
  const investmentDecisionMaker = getInvestmentDecisionMaker(negotiationOrDeal, self)
  return isNil(investmentDecisionMaker) ? '' : DecisionMakerHelper.getName(investmentDecisionMaker)
}

export function getInvestmentDecisionMaker(negotiationOrDeal: NegotiationOrDealDetail, self: boolean): User | null {
  const { ownOrder, initiatorInvestmentDecisionMaker, responderInvestmentDecisionMaker } = negotiationOrDeal

  if ((ownOrder && self) || (!ownOrder && !self)) {
    return initiatorInvestmentDecisionMaker
  } else {
    return responderInvestmentDecisionMaker
  }
}

export function getServicingDealExecutionDecisionMakerId(dealDetail: DealDetail, self: boolean): string | null {
  const { ownOrder, servicingSideDetails } = dealDetail
  const initiatorExecutionDecisionMakerId = getNegotiatedValue(
    servicingSideDetails?.negotiationFields.INITIATOR_EXECUTION_DECISION_MAKER?.negotiableFieldRequired
  )
  const responderExecutionDecisionMakerId = getNegotiatedValue(
    servicingSideDetails?.negotiationFields.RESPONDER_EXECUTION_DECISION_MAKER?.negotiableFieldRequired
  )

  if ((ownOrder && self) || (!ownOrder && !self)) {
    return initiatorExecutionDecisionMakerId
  } else {
    return responderExecutionDecisionMakerId
  }
}

export function getExecutionDecisionMakerName(negotiationOrDeal: NegotiationOrDealDetail, self: boolean): string {
  const executionDecisionMaker = getExecutionDecisionMaker(negotiationOrDeal, self)
  return isNil(executionDecisionMaker) ? '' : DecisionMakerHelper.getName(executionDecisionMaker)
}

export function getExecutionDecisionMaker(negotiationOrDeal: NegotiationOrDealDetail, self: boolean): User | null {
  const { ownOrder, initiatorExecutionDecisionMaker, responderExecutionDecisionMaker } = negotiationOrDeal

  if ((ownOrder && self) || (!ownOrder && !self)) {
    return initiatorExecutionDecisionMaker
  } else {
    return responderExecutionDecisionMaker
  }
}

export function makeTradingEntityLeiGetter(self: boolean, rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>) {
  return function tradingEntityGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getTradingEntityLei(rowData, self)
    } else {
      return ''
    }
  }
}

export function makeTradingEntityFieldGetter(
  field: SupportedTradingEntityFieldNames,
  self: boolean,
  rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>
) {
  return function tradingEntityGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getTradingEntityField(rowData, field, self)
    } else {
      return ''
    }
  }
}

export function getTradingEntityName(negotiationOrDeal: NegotiationOrDealDetail, self: boolean) {
  const { ownOrder, orderFund, counterparty, clearingHouse } = negotiationOrDeal
  if (ownOrder) {
    if (self) {
      return isNil(orderFund) ? '' : orderFund.publishedName
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal)
        ? isNil(counterparty)
          ? ''
          : TradingEntityWithAliasHelper.getName(counterparty)
        : clearingHouse.publishedName
    }
  } else {
    if (self) {
      return isNil(counterparty) ? '' : counterparty.publishedName
    } else {
      return isNil(clearingHouse) || isUsMarket(negotiationOrDeal)
        ? isNil(orderFund)
          ? ''
          : TradingEntityWithAliasHelper.getName(orderFund)
        : clearingHouse.publishedName
    }
  }
}

export function getExecutingTradingEntityName(negotiationOrDeal: NegotiationOrDealDetail) {
  const { ownOrder, orderFund, counterparty, clearingHouse } = negotiationOrDeal
  if (ownOrder) {
    return !isNil(clearingHouse) && !isUsMarket(negotiationOrDeal)
      ? isNil(counterparty)
        ? ''
        : TradingEntityWithAliasHelper.getName(counterparty)
      : ''
  } else {
    return !isNil(clearingHouse) && !isUsMarket(negotiationOrDeal)
      ? isNil(orderFund)
        ? ''
        : TradingEntityWithAliasHelper.getName(orderFund)
      : ''
  }
}

export function getSponsorName(negotiationOrDeal: NegotiationOrDealDetail, self: boolean) {
  const { ownOrder, sponsor, counterpartySponsor } = negotiationOrDeal
  if (ownOrder) {
    if (self) {
      return isNil(sponsor) ? '' : sponsor.publishedName
    } else {
      return isNil(counterpartySponsor) ? '' : counterpartySponsor.publishedName
    }
  } else {
    if (self) {
      return isNil(counterpartySponsor) ? '' : counterpartySponsor.publishedName
    } else {
      return isNil(sponsor) ? '' : sponsor.publishedName
    }
  }
}

export function makeSponsorNameGetter(self: boolean, rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>) {
  return function sponsorNameGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getSponsorName(rowData, self)
    } else {
      return ''
    }
  }
}

export function makeTradingEntityNameGetter(self: boolean, rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>) {
  return function tradingEntityNameGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getTradingEntityName(rowData, self)
    } else {
      return ''
    }
  }
}

export function preTradeOrTradingEntityNameGetter(self: boolean, negotiationDetail: NegotiationDetail) {
  const isPreTradeNegotiation = negotiationDetail?.negotiation.nestedLegs?.legs.length
  return isPreTradeNegotiation ? Locale.translate(Translation.TC_PRE_ASSIGNED) : getTradingEntityName(negotiationDetail, self)
}

export function makeExecutingTradingEntityNameGetter(rowDataGetter?: RowDataGetter<NegotiationOrDealDetail>) {
  return function executingTradingEntityNameGetter(params: TypedValueGetterParams<NegotiationOrDealDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    if (rowData) {
      return getExecutingTradingEntityName(rowData)
    } else {
      return ''
    }
  }
}

export function makeExternalNegotiationIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function externalNegotiationIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData
      ? rowData.collection?.type === CollectionType.ALL_OR_NOTHING || rowData.collection?.type === CollectionType.ONE_CANCELS_OTHERS
        ? rowData.negotiation.externalSummaryNegotiationId
        : rowData.negotiation.externalNegotiationId
      : ''
  }
}

export function makeExternalQuoteRespIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function externalQuoteRespIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData?.negotiation.externalQuoteResponseId ? rowData.negotiation.externalQuoteResponseId : rowData?.negotiation.negotiationId
  }
}

export function makeExternalOrderIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function externalOrderIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? rowData.negotiation.externalOrderId : ''
  }
}

export function makeOrderIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function OrderIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? rowData.negotiation.parentOrderId : ''
  }
}

export function makeExternalQuoteIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function externalQuoteIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? rowData.negotiation.externalQuoteId : ''
  }
}

export function makeExternalQuoteResponseIdGetter(rowDataGetter?: RowDataGetter<NegotiationDetail>) {
  return function externalQuoteResponseIdGetter(params: TypedValueGetterParams<NegotiationDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    return rowData ? rowData.negotiation.externalQuoteResponseId : ''
  }
}

function makeFormatData(dataHelper: DataHelper<any>) {
  return function formatData(value: string | null) {
    return isNil(value) ? '' : dataHelper.getName(value)
  }
}

function makeDataFormatter(dataHelper: DataHelper<any>) {
  return function dataFormatter(params: ValueFormatterParams) {
    return makeFormatData(dataHelper)(params.value)
  }
}

export const formatRepoType = makeFormatData(RepoTypeHelper)
export const formatCouponFrequency = makeFormatData(CouponFrequencyHelper)
export const callableTypeFormatter = makeDataFormatter(CallableTypeHelper)
export const collateralTypeFormatter = makeDataFormatter(CollateralTypeHelper)
export const collateralIdentifierTypeFormatter = makeDataFormatter(CollateralIdentifierTypeHelper)
export const collectionTypeFormatter = makeDataFormatter(CollectionTypeHelper)
export const couponFrequencyFormatter = makeDataFormatter(CouponFrequencyHelper)
export const couponTypeFormatter = makeDataFormatter(RateTypeHelper)
export const haircutTypeFormatter = makeDataFormatter(HaircutTypeHelper)
export const indexLagFormatter = makeDataFormatter(IndexLagHelper)
export const orderTermFormatter = makeDataFormatter(OrderTermHelper)
export const evergreenNoticeFormatter = makeDataFormatter(EvergreenNoticeHelper)
export const productPermissionFormatter = makeDataFormatter(TradingEntityProductPermissionHelper)
export const repoClassificationFormatter = makeDataFormatter(RepoClassificationHelper)
export const repoTypeFormatter = makeDataFormatter(RepoTypeHelper)
export const rfqTypeFormatter = makeDataFormatter(RFQTypeHelper)
export const assetTypeFormatter = makeDataFormatter(AssetTypeHelper)
export const servicingTypeFormatter = makeDataFormatter(ServicingTypeHelper)
export const tradingTypeFormatter = makeDataFormatter(TradingTypeHelper)
export const formatHaircutType = makeFormatData(HaircutTypeHelper)
export const startTimeZoneFormatter = makeDataFormatter(StartTimeZoneHelper)
export const marketTypeFormatter = makeDataFormatter(MarketTypeHelper)

export const defaultFormatter = (params: ValueFormatterParams) => params.value

export const booleanFormatter = (params: ValueFormatterParams) =>
  isNil(params.value) ? '' : params.value ? Locale.translate(Translation.TC_TRUE) : Locale.translate(Translation.TC_FALSE)

export const booleanValueFormatter = (value: string) => {
  return value === '1' ? Locale.translate(Translation.TC_TRUE) : Locale.translate(Translation.TC_FALSE)
}

export const complianceValidFormatter = (value: boolean | undefined) =>
  isNil(value) ? '' : value ? Locale.translate(Translation.TC_VALID) : Locale.translate(Translation.TC_INVALID)

export const listFormatter = (params: ValueFormatterParams) =>
  isNil(params.value) || !isArray(params.value) ? '' : params.value.sort((a, b) => compareInsensitive(a, b)).join(', ')

export function makeFormatDirection(directionFormat: DirectionFormat) {
  return makeFormatData(makeDirectionHelper(directionFormat))
}

export function directionFormatter(params: ValueFormatterParams) {
  const { context, value } = params
  return makeFormatData(makeDirectionHelper(context.userPreference.directionFormat))(value)
}

export function formatTerm(value: OrderTerm | null) {
  return isNil(value) ? '' : makeFormatData(OrderTermHelper)(value)
}

export function formatEvergreenNotice(value: EvergreenNoticePeriod | null) {
  return isNil(value) ? '' : makeFormatData(EvergreenNoticeHelper)(value)
}

export function issueFormatter(params: ValueFormatterParams) {
  return makeFormatData(IssueHelper)(params.value)
}

export function formatOrganisationTypes(values: OrganisationType[] | null, separator: string = ', ') {
  return isNil(values) || !isArray(values) ? '' : values.map(value => makeFormatData(OrganisationTypeHelper)(value)).join(separator)
}

export function tradingEntityTypesFormatter(params: ValueFormatterParams) {
  if (isNil(params.value) || params.value.length === 0) {
    return ''
  }
  if (params.value.length === 2) {
    return Locale.translate(Translation.TC_BOTH)
  } else if (params.value.includes(TradingEntityType.BLOCK)) {
    return Locale.translate(Translation.TC_BLOCK)
  } else {
    return Locale.translate(Translation.TC_STANDARD)
  }
}

export function getOwnDirection(value: Direction, ownOrder: boolean): Direction
export function getOwnDirection(value: Direction | null, ownOrder: boolean): Direction | null
export function getOwnDirection(value: Direction | null | undefined, ownOrder: boolean): Direction | null
export function getOwnDirection(value?: Direction | null, ownOrder?: boolean): Direction | null
export function getOwnDirection(value?: Direction | null, ownOrder?: boolean) {
  if (isNil(value) || isNil(ownOrder)) {
    return value
  } else if (ownOrder) {
    return value
  } else if (value === Direction.BUY) {
    return Direction.SELL
  } else if (value === Direction.SELL) {
    return Direction.BUY
  } else {
    throw new Error(`Unhandled Direction ${[value]}`)
  }
}

export function getFormattedDirection(value: Direction | null, ownOrder: boolean, directionFormat: DirectionFormat) {
  return makeFormatDirection(directionFormat)(getOwnDirection(value, ownOrder))
}

export function getRateFormatOptions(referenceRate: ReferenceRate | null, sign?: boolean): NumberFormatOptions {
  if (referenceRate) {
    return { minDp: 1, decimalShift: 4, basisPoints: true, sign: sign }
  } else {
    return { minDp: 3, decimalShift: 2, percent: true, sign: sign }
  }
}

export function getFeeFormatOptions() {
  return { minDp: 1, decimalShift: 4, basisPoints: true }
}

export function getRatePercentFormat(sign?: boolean): NumberFormatOptions {
  return { minDp: 3, decimalShift: 2, percent: true, sign: sign, maxDp: 3 }
}

export function makeRateFormatter(withSign?: boolean, rowDataGetter?: RowDataGetter<TradeDetail>) {
  return function rateFormatter(params: ValueFormatterParams): string {
    const { node, data, value } = params
    const rowData = rowDataGetter ? rowDataGetter(params) : data
    if (isNil(value)) {
      return ''
    } else if (rowData) {
      const formatOptions = getRateFormatOptions(rowData.referenceRate)
      formatOptions.sign = withSign
      return rowData.ownOrder === false && node?.group
        ? Locale.formatNumber(Decimal.multiply(value, -1), formatOptions)
        : Locale.formatNumber(value, formatOptions)
    } else {
      return ''
    }
  }
}

export function makeRateFilterGetter(rowDataGetter?: RowDataGetter<TradeDetail>) {
  return function rateFilterGetter(params: TypedValueGetterParams<TradeDetail>) {
    const rowData = rowDataGetter ? rowDataGetter(params) : params.data
    const formatOptions = getRateFormatOptions((rowData?.referenceRate as ReferenceRate) ?? null)
    const numberFilterGetter = makeNumberFilterGetter(formatOptions)
    return numberFilterGetter(params)
  }
}

export function makeFeeFormatter() {
  return function feeFormatter(params: ValueFormatterParams): string {
    return isNotNil(params.value) ? Locale.formatNumber(params.value, getFeeFormatOptions()) : ''
  }
}

export function getNotionalFormatOptions(
  amountFormat: AmountFormat,
  showDecimalShiftSuffix?: boolean,
  sign?: boolean,
  parenOnNegative?: boolean
): NumberFormatOptions {
  switch (amountFormat) {
    case AmountFormat.ACTUAL:
      return { minDp: 0, maxDp: 0, sign, parenOnNegative }
    case AmountFormat.THOUSANDS:
      return { minDp: 0, maxDp: 3, decimalShift: -3, showDecimalShiftSuffix, sign, parenOnNegative }
    case AmountFormat.MILLIONS:
      return { minDp: 0, maxDp: 6, decimalShift: -6, showDecimalShiftSuffix, sign, parenOnNegative }
    default:
      throw new Error(`Unhandled AmountFormat ${[amountFormat]}`)
  }
}

export function makeNotionalFormatter(withSign?: boolean, rowDataGetter?: RowDataGetter<TradeDetail>) {
  return function notionalFormatter(params: ValueFormatterParams): string {
    const { value, node, data, context } = params
    const rowData = rowDataGetter ? rowDataGetter(params) : data
    if (isNil(value)) {
      return ''
    }

    const formatOptions = getNotionalFormatOptions(context.userPreference.amountFormat, false, withSign, !!node?.group)

    if (rowData) {
      // summary row on responder side -> sum of notionals should be flipped
      return rowData.ownOrder === false && node?.group
        ? Locale.formatNumber(Decimal.multiply(value, -1), formatOptions)
        : Locale.formatNumber(value, formatOptions)
    }

    return Locale.formatNumber(value, formatOptions)
  }
}

export function makeNotionalFilterGetter(withSign?: boolean) {
  return function notionalFilterGetter(params: TypedValueGetterParams<TradeDetail>) {
    const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat, false, withSign)
    const numberFilterGetter = makeNumberFilterGetter(formatOptions)
    return numberFilterGetter(params)
  }
}

export function notionalHeaderValueGetter(params: TypedHeaderValueGetterParams<TradeDetail>) {
  return Locale.translate(Translation.TC_NOTIONAL, { amountFormat: params.context.userPreference.amountFormat })
}

export function newNotionalHeaderValueGetter(params: TypedHeaderValueGetterParams<TradeDetail>) {
  return Locale.translate(Translation.TC_NEW_NOTIONAL, { amountFormat: params.context.userPreference.amountFormat })
}

export const getStartConsiderationFormatOptions = (sign?: boolean) => ({ minDp: 0, sign })

export function makeStartConsiderationFormatter(withSign?: boolean, rowDataGetter?: RowDataGetter<TradeDetail>) {
  return function startConsiderationFormatter(params: ValueFormatterParams): string {
    const { value, data } = params
    const rowData = rowDataGetter ? rowDataGetter(params) : data
    if (isNil(value)) {
      return ''
    } else if (rowData) {
      return Locale.formatNumber(value, getStartConsiderationFormatOptions(withSign))
    } else {
      return ''
    }
  }
}

export function makeNumberFormatterSummaryRow(
  options?: NumberFormatOptions,
  rowDataGetter?: RowDataGetter<TradeDetail>,
  parenOnNegative?: boolean
) {
  return function numberFormatter(params: ValueFormatterParams) {
    const { value, data, node } = params
    const rowData = rowDataGetter ? rowDataGetter(params) : data
    if (isNil(value)) {
      return ''
    }

    if (rowData) {
      const formatOptions = {
        ...options,
        ...(node?.group && parenOnNegative && { parenOnNegative: true })
      }
      return rowData.ownOrder === false && node?.group
        ? Locale.formatNumber(Decimal.multiply(value, -1), formatOptions)
        : Locale.formatNumber(value, formatOptions)
    }

    return ''
  }
}

export function makeStartConsiderationFilterGetter(withSign?: boolean) {
  return function startConsiderationFilterGetter(params: TypedValueGetterParams<TradeDetail>) {
    const numberFilterGetter = makeNumberFilterGetter(getStartConsiderationFormatOptions(withSign))
    return numberFilterGetter(params)
  }
}

export const getHaircutFormatOptions = (
  sign?: boolean,
  haircutSign?: HaircutSign,
  ownOrder?: boolean,
  direction?: Direction | null,
  delta?: boolean
): NumberFormatOptions => ({
  minDp: 3,
  percent: true,
  decimalShift: 2,
  sign,
  flipSign:
    (haircutSign === HaircutSign.DIRECTIONAL && ownOrder) ||
    (delta && getOwnDirection(direction, ownOrder) === Direction.BUY) ||
    (haircutSign === HaircutSign.NEGATIVE_REV_REPO && ownOrder && getOwnDirection(direction, ownOrder) === Direction.BUY),
  absoluteIncrease: !delta && haircutSign === HaircutSign.ABSOLUTE && getOwnDirection(direction, ownOrder) === Direction.SELL,
  absoluteDecrease: !delta && haircutSign === HaircutSign.ABSOLUTE && getOwnDirection(direction, ownOrder) === Direction.BUY
})

export function makeHaircutFormatter(withSign?: boolean, delta?: boolean) {
  return function haircutFormatter(params: ValueFormatterParams): string {
    if (isNil(params.value)) {
      return ''
    } else {
      return Locale.formatNumber(
        params.value,
        getHaircutFormatOptions(withSign, params.context.userPreference.haircutSign, params.data?.ownOrder, params.data?.direction, delta)
      )
    }
  }
}

export function makeHaircutFilterGetter(withSign?: boolean, delta?: boolean) {
  return function haircutFilterGetter(params: TypedValueGetterParams<NegotiationDetail | AuditDetail | DealDetail>) {
    const numberFilterGetter = makeNumberFilterGetter(
      getHaircutFormatOptions(withSign, params.context.userPreference.haircutSign, params.data?.ownOrder, params.data?.direction, delta)
    )
    return numberFilterGetter(params)
  }
}

// Quick Filter Text Getter

export function directionQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<Direction>) {
  return makeFormatDirection(params.context.userPreference.directionFormat)(params.value)
}

export function orderTermQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<OrderTerm>) {
  const termStr = formatTerm(params.value)
  return `${termStr} ${termStr.replace(/\s+/g, '')}`
}

export function evergreenNoticePeriodQuickfixFilterTextGetter(params: TypedGetQuickFilterTextParams<EvergreenNoticePeriod>) {
  return formatEvergreenNotice(params.value)
}

export function servicingTypeQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<ServicingType>) {
  return servicingTypeFormatter(params)
}

export function makeHaircutQuickFilterTextGetter(withSign?: boolean) {
  return function haircutQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<number>) {
    const value = Locale.formatNumber(params.value, { minDp: 3, percent: true, sign: withSign })
    return value
  }
}

export function makeRateQuickFilterTextGetter(withSign?: boolean) {
  return function rateQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<number>) {
    return params.data
      ? Locale.formatNumber(params.value, {
          percent: !params.data.referenceRate,
          basisPoints: !!params.data.referenceRate,
          sign: withSign
        })
      : ''
  }
}

export function makePrevRateFilterGetter() {
  return function rateFilterGetter(params: ValueGetterParams) {
    if (typeof params.colDef.valueGetter !== 'function') {
      throw new Error('valueGetter must be a function to create a filterValueGetter')
    }

    const value = params.colDef.valueGetter(params)

    return makeRateFormatter()({
      value,
      ...params
    })
  }
}

export function axeTradeConditionFilterTextGetter(
  params: TypedGetQuickFilterTextParams<AxeTradeCondition> | ValueFormatterParams<AxeTradeCondition>
) {
  if (isNil(params.value) || isNil(params.value.tradeCondition) || isNil(params.value.isAxed) || params.node?.group) {
    return ''
  } else {
    return `${AxeHelper.getName(params.value.isAxed)} ${TradeConditionFlagHelper.getName(params.value.tradeCondition)}`
  }
}

export function axeFilterValueFormatter(params: ValueFormatterParams) {
  const isAxed = params.value.isAxed
  return isAxed ? AxeHelper.getName(isAxed) : TradeConditionFlagHelper.getName(params.value.tradeCondition)
}

export function counterPartyNameFormatter(params: ValueFormatterParams) {
  return params.value
    .filter((val: any) => val?.isAxed || val?.tradeCondition !== TradeConditionFlag.NONE)
    .map((val: any) =>
      [
        AxeHelper.getName(val.isAxed),
        val?.tradeCondition !== TradeConditionFlag.NONE ? TradeConditionFlagHelper.getName(val.tradeCondition) : '',
        val?.publishedName
      ].join(' ')
    )
    .join(', ')
}

export function couponFrequencyQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<CouponFrequency>) {
  return formatCouponFrequency(params.value)
}

export function rfqTimeQuickFilterTextGetter(params: TypedGetQuickFilterTextParams<number>) {
  return isNil(params.value) ? '' : getFormattedTime(params.value)
}

export function getClearingStatus(negotiationOrDeal: NegotiationOrDealDetail) {
  const { clearingHouse } = negotiationOrDeal
  return isNil(clearingHouse) ? Locale.translate(Translation.TC_FALSE) : Locale.translate(Translation.TC_TRUE)
}

export function getTradingStatus(tradingStatus: TradingStatus | undefined) {
  return isNil(tradingStatus) ? Translation.TC_DISABLED : tradingStatusTranslations[tradingStatus]
}

export function getNegotiationClearingStatusForNonSummary(negotiationDetail: NegotiationDetail) {
  const notSummaryNegotiationType = ![
    NegotiationType.CONTINGENT_SUMMARY,
    NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
    NegotiationType.SMART_GROUPING_SUMMARY,
    NegotiationType.AON_X_SUMMARY
  ].includes(negotiationDetail.negotiation.negotiationType)
  return notSummaryNegotiationType ? getClearingStatus(negotiationDetail) : null
}

export function getSettlementVenueName(settlementVenue: string) {
  return SettlementVenueHelper.getName(settlementVenue as SettlementVenue)
}

// Transform Input

// Rate Input

export function transformRateInput(rate: number, referenceRate: ReferenceRate | null): number {
  return Decimal.shift(rate, referenceRate ? BASIS_POINTS_DECIMAL_SHIFT : PERCENTAGE_DECIMAL_SHIFT)
}

// Haircut Input

export function transformHaircutInput(haircut: number, haircutSign: HaircutSign, ownOrder: boolean, direction: Direction | null): number {
  if (
    (haircutSign === HaircutSign.DIRECTIONAL && ownOrder) ||
    (haircutSign === HaircutSign.NEGATIVE_REV_REPO && ownOrder && getOwnDirection(direction, ownOrder) === Direction.BUY)
  ) {
    haircut = Decimal.multiply(haircut, -1)
  } else if (haircutSign === HaircutSign.ABSOLUTE && getOwnDirection(direction, ownOrder) === Direction.SELL) {
    haircut = Decimal.subtract(haircut, 100)
  } else if (haircutSign === HaircutSign.ABSOLUTE && getOwnDirection(direction, ownOrder) === Direction.BUY) {
    haircut = Decimal.subtract(100, haircut)
  }
  return Decimal.shift(haircut, -2)
}
